import { useRef, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Image as ImageIcon, Loader2, X, Upload, Link as LinkIcon } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Admin-only "Workout cover photo" picker. Lets the admin either:
 *   - paste an existing image URL (Unsplash, their own CDN, etc.) OR
 *   - upload a local file directly to S3 via a presigned URL
 *
 * The chosen URL is persisted to the workout's `image_url` field — the same
 * field rendered on the public workout card.
 */
export const ImageUploadSection = ({ workout, updateField, isNew }) => {
  const { api } = useAuth();
  const [mode, setMode] = useState('url');
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  /** Persist just the cover photo to the DB so admins don't have to remember
   *  to click "Save Changes" at the top after picking a photo. Skipped for
   *  brand-new workouts (no doc to PATCH yet — Save Changes creates it). */
  const persistImage = async (imageUrl) => {
    if (isNew || !workout?.id) return;
    try {
      await api().put(`/admin/workouts/${workout.id}`, { image_url: imageUrl });
    } catch (err) {
      toast.error('Photo uploaded but failed to save — hit "Save Changes" to retry');
    }
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowed = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'image/jpg'];
    if (!allowed.includes(file.type) && !/\.(jpe?g|png|webp|gif)$/i.test(file.name)) {
      toast.error('Please choose a JPG, PNG, WEBP, or GIF image.');
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error('Image is over 8 MB — please pick a smaller file.');
      return;
    }

    setUploading(true);
    try {
      // Backend-proxy upload (same-origin → no S3 CORS headaches). Server-side
      // boto3 puts the file in S3 and returns the public URL.
      const form = new FormData();
      form.append('file', file);
      const res = await api().post('/admin/images/upload', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const public_url = res.data?.public_url;
      if (!public_url) throw new Error('Upload succeeded but no URL returned');

      updateField('image_url', public_url);
      await persistImage(public_url);
      toast.success('Cover photo saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const clearImage = async () => {
    updateField('image_url', '');
    await persistImage('');
    toast.info('Cover photo removed');
  };

  const currentUrl = workout.image_url || '';

  return (
    <div className="bg-card border border-border p-6" data-testid="workout-image-section">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-heading text-lg text-white uppercase tracking-wider flex items-center gap-2">
          <ImageIcon className="h-5 w-5 text-primary" />
          Cover Photo
        </h3>
        <div className="flex gap-2">
          <Button
            type="button"
            size="sm"
            variant={mode === 'url' ? 'default' : 'outline'}
            className={mode === 'url' ? 'bg-primary text-black' : 'border-border'}
            onClick={() => setMode('url')}
            data-testid="image-mode-url-btn"
          >
            <LinkIcon className="h-4 w-4 mr-1" /> Image URL
          </Button>
          <Button
            type="button"
            size="sm"
            variant={mode === 'upload' ? 'default' : 'outline'}
            className={mode === 'upload' ? 'bg-primary text-black' : 'border-border'}
            onClick={() => setMode('upload')}
            data-testid="image-mode-upload-btn"
          >
            <Upload className="h-4 w-4 mr-1" /> Upload Photo
          </Button>
        </div>
      </div>

      <p className="text-muted-foreground text-sm mb-4">
        Shows up at the top of the workout card. Aim for a 16:9 action shot — the user's
        first impression of this workout.
      </p>

      {/* Preview */}
      {currentUrl ? (
        <div className="relative mb-4" data-testid="workout-image-preview-wrap">
          <img
            src={currentUrl}
            alt="Cover preview"
            className="w-full h-48 object-cover rounded-md border border-border"
            data-testid="workout-image-preview"
          />
          <button
            type="button"
            onClick={clearImage}
            className="absolute top-2 right-2 w-8 h-8 rounded-full bg-black/70 hover:bg-black flex items-center justify-center text-white"
            data-testid="workout-image-clear-btn"
            aria-label="Remove cover photo"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      ) : (
        <div className="w-full h-48 mb-4 border border-dashed border-border rounded-md flex items-center justify-center text-muted-foreground bg-black/30">
          <div className="text-center">
            <ImageIcon className="h-8 w-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">No cover photo yet</p>
          </div>
        </div>
      )}

      {mode === 'url' ? (
        <Input
          type="url"
          value={currentUrl || ''}
          onChange={(e) => updateField('image_url', e.target.value)}
          onBlur={(e) => persistImage(e.target.value)}
          placeholder="https://images.unsplash.com/photo-... or your CDN URL"
          className="bg-black border-border text-white"
          data-testid="workout-image-url-input"
        />
      ) : (
        <div className="flex items-center gap-3">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/jpg,image/png,image/webp,image/gif"
            onChange={handleFile}
            className="hidden"
            data-testid="workout-image-file-input"
          />
          <Button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="bg-primary text-black hover:bg-primary/90"
            data-testid="workout-image-upload-btn"
          >
            {uploading ? (
              <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Uploading…</>
            ) : (
              <><Upload className="h-4 w-4 mr-2" /> Choose Photo (JPG/PNG/WEBP, max 8MB)</>
            )}
          </Button>
        </div>
      )}
    </div>
  );
};
